import { useCallback, useEffect, useMemo } from "react";

import { PlusIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useInView } from "react-intersection-observer";

import Button from "@gx/dls/buttons/Button";
import FlexWrapper, { FlexDirection } from "@gx/dls/containers/FlexWrapper";

import EmptyLayout from "@/layouts/EmptyLayout";

import SelectProjectSourceCard from "@/pages/projects/forms/create/components/steps/select-project-sources/components/SelectProjectSourceCard";
import SelectProjectSourceCardLoading from "@/pages/projects/forms/create/components/steps/select-project-sources/components/SelectProjectSourceCardLoading";
import { useCreateProjectFormContext } from "@/pages/projects/forms/create/hooks";

import { useListSourcesInfiniteQuery } from "@/api/queries/sources";

interface SelectProjectSourcesBodyProps {
  existingSourceIds?: Set<string>;
}

const SelectProjectSourcesBody = ({ existingSourceIds }: SelectProjectSourcesBodyProps) => {
  const navigate = useNavigate();
  const { state } = useCreateProjectFormContext();

  const { ref: infiniteScrollRef, inView } = useInView({
    threshold: 0,
    rootMargin: "100px",
  });

  const {
    data,
    isLoading: isLoadingSources,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useListSourcesInfiniteQuery({});

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const sources = useMemo(() => data?.pages.flatMap((p) => p.sources) ?? [], [data]);
  const sortedSources = useMemo(() => {
    if (!sources.length) return [];
    return sources.filter((source) => {
      const matchesSearch =
        !state.search || source.name.toLowerCase().includes(state.search.toLowerCase());
      const matchesSourceType = state.selectedSourceTypes.includes(source.sourceType);
      const matchesStatus = state.selectedStatuses.includes(source.sourceStatus);
      return matchesSearch && matchesSourceType && matchesStatus;
    });
  }, [sources, state.search, state.selectedSourceTypes, state.selectedStatuses]);

  const handleNewSourceClick = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, createSource: true }),
    });
  }, [navigate]);

  if (isLoadingSources) {
    return (
      <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth>
        {Array.from({ length: 2 }).map((_, index) => (
          <SelectProjectSourceCardLoading key={index} />
        ))}
      </FlexWrapper>
    );
  }

  if (sources.length === 0 || sortedSources.length === 0) {
    return (
      <EmptyLayout
        title="No sources found"
        description="Add a source to your project"
        actions={<Button label="New source" icon={PlusIcon} onClick={handleNewSourceClick} />}
      />
    );
  }

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth>
      {sortedSources.map((source) => (
        <SelectProjectSourceCard
          key={source.id}
          source={source}
          isExisting={existingSourceIds?.has(source.id)}
        />
      ))}
      {isFetchingNextPage &&
        Array.from({ length: 2 }).map((_, index) => (
          <SelectProjectSourceCardLoading key={`loading-${index}`} />
        ))}
      <div ref={infiniteScrollRef} style={{ minHeight: 1 }} />
    </FlexWrapper>
  );
};

export default SelectProjectSourcesBody;
